import React, { useEffect, useState } from 'react';
import {
    Badge,
    Box,
    Icon,
    Heading,
    Stack,
    Text,
    useColorModeValue
} from '@chakra-ui/react';
import DataTable from 'react-data-table-component';
import DataTableExtensions from 'react-data-table-component-extensions';
import 'react-data-table-component-extensions/dist/index.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getPaises, reset } from '../../features/paisSlice';
import { ToastChakra } from '../../helpers/toast';
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import { customStyles } from '../../helpers/customStyles';
import { ModalAgregarPais } from './ModalAgregarPais';
import { ModalDetallesPais } from './ModalDetallesPais';
import { ModalEditarPais } from './ModalEditarPais';
import { AlertEliminarPais } from './AlertEliminarPais';
import '../../theme/solarizedTheme';
import { Loading } from '../../helpers/Loading';
import Moment from 'moment';

const Paises = ({ isRTL = false, ...props }) => {
    const direction = isRTL ? 'rtl' : 'ltr';

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const themeTable = useColorModeValue('default', 'solarized');

    const { user } = useSelector((state) => state.auth);

    const { paises, isError, isLoading, message, totalRows } = useSelector((state) => state.paises);

    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);

    useEffect(() => {
        if (!user) {
            navigate("/login");
            return;
        }
        dispatch(getPaises({ page: 1, perPage })); // Solo llamamos la primera vez o si cambia `perPage`

        return () => {
            dispatch(reset()); // Reseteamos al desmontar el componente
        };
    }, [user, navigate, dispatch, perPage]);

    if (isError) {
        ToastChakra('Error', message, 'error', 1500);
        console.log(message);
    }
    

    const columns = [
        {
            name: 'ID',
            selector: row => row?.id || 'N/A',
            sortable: true,
            cellExport: row => row?.id || 'N/A',
            omit: true // Esto oculta la columna
        },
        {
            name: 'DESCRIPCION',
            selector: row => row?.descripcion,
            sortable: true,
            cellExport: row => row?.descripcion,
       
        },
        {
            name: 'FECHA ALTA',
            selector: row => Moment(row?.createdAt).format('DD-MM-YYYY hh:mm A'),
            sortable: true,
            cellExport: row => Moment(row?.createdAt).format('DD-MM-YYYY hh:mm A'),
            resizable: true,

        },
        {
            name: 'FECHA MODIFICACION',
            selector: row => Moment(row?.updatedAt).format('DD-MM-YYYY hh:mm A'),
            sortable: true,
            cellExport: row => Moment(row?.updatedAt).format('DD-MM-YYYY hh:mm A'),
            resizable: true,

        },
        {
            name: 'ESTADO',
            selector: row => { return row.estado === true ? 'ACTIVO' : 'INACTIVO' },
            sortable: true,
            cellExport: row => row.estado === true ? 'ACTIVO' : 'INACTIVO',
            center: true,
            cell: row => (
                <div>
                    <Badge
                        colorScheme={row.estado === true ? 'green' : 'red'}
                        variant="solid"
                        w={24}
                        textAlign="center"
                        py={2}
                        rounded="full"
                    >
                        {row.estado === true ? 'ACTIVO' : 'INACTIVO'}
                    </Badge>
                </div>
            )
        },
        {
            name: 'ACCIONES',
            export: false,
            center: true,
            cell: row => (
                <div>
                    <ModalDetallesPais pais={row} />
                    <ModalEditarPais row={row} page={page} perPage={perPage} />
                    <AlertEliminarPais row={row} page={page} perPage={perPage} />
                </div>
            ),
            width: '220px'
        }
    ]

    const handlePageChange = (newPage) => {
        setPage(newPage); // Actualiza el estado local de la página
        dispatch(getPaises({ page: newPage, perPage })); // Despacha la acción con la nueva página
    };

    const handleRowsPerPageChange = (newPerPage) => {
        setPerPage(newPerPage); // Cambiamos el número de filas por página
        dispatch(getPaises({ page: 1, perPage: newPerPage })); // Reiniciamos a la primera página
    };

    const tableData = {
        columns: columns,
        data: paises,
    }

    if(isLoading) {
        return <Loading />
    }

    return (
        <div style={{ direction }} {...props}>

            <Stack spacing={4} direction="row" justifyContent="space-between" py={4} px={4}>
                <Heading size="lg">Países</Heading>
                <ModalAgregarPais page={page} perPage={perPage} />
            </Stack>
            <Box
                overflow="hidden"
                boxShadow={'base'}
                bg="white"
                _dark={{ bg: "primary.1000" }}
                mt={2}
                pt={2}
                rounded="2xl"
            >
                <DataTableExtensions
                    {...tableData}
                    print={false}
                    exportHeaders={true}
                    filterPlaceholder="BUSCAR"
                    numberOfColumns={columns.length}
                    fileName={'PAISES_' + new Date().toLocaleDateString()}
                >
                    <DataTable
                        defaultSortField="createdAt"
                        defaultSortAsc={false}
                        defaultSortOrder="desc"
                        pagination
                        paginationIconFirstPage={<Icon as={FiChevronsLeft} boxSize={6} _dark={{ color: "gray.300" }} />}
                        paginationIconLastPage={<Icon as={FiChevronsRight} boxSize={6} _dark={{ color: "gray.300" }} />}
                        paginationIconPrevious={<Icon as={FiChevronLeft} boxSize={6} _dark={{ color: "gray.300", _hover: { color: "white" } }} />}
                        paginationIconNext={<Icon as={FiChevronRight} boxSize={6} _dark={{ color: "gray.300", _hover: { color: "white" } }} />}
                        paginationServer
                        paginationPerPage={perPage}
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30, 50]}
                        paginationDefaultPage={page}
                        paginationTotalRows={totalRows}
                        onChangePage={handlePageChange}
                        paginationComponentOptions={{
                            rowsPerPageText: 'Filas por página:',
                            rangeSeparatorText: 'de',
                            noRowsPerPage: false,
                            selectAllRowsItem: true,
                            selectAllRowsItemText: 'Todos',
                        }}
                        theme={themeTable}
                        customStyles={customStyles}
                        pointerOnHover={true}
                        responsive={true}
                        noDataComponent={<Text mb={4} fontSize="lg">NO SE ENCONTRARON DATOS</Text>}
                    />
                </DataTableExtensions>
            </Box>
       </div> 
    );
}

export default Paises;